import { Icon } from "~/react/ui/icon"
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "~/react/ui/tooltip"
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "~/react/ui/table"

declare const i18n: {
  t: (key: string, options?: Record<string, unknown>) => string
}

interface FollowUp {
  name: string
  step: number
  subject: string
  pending: number
  messages_sent: number
  messages_delivered: number
  opens: number
  clicked: number
  replied: number
  bounced: number
  unsubscribed: number
  successful: number
  open_rate: number
  click_rate: number
  reply_rate: number
  bounce_rate: number
  unsubscribe_rate: number
  successful_rate: number
}

interface StatisticsTableProps {
  campaign: {
    pending_count: number
    sent_count: number
    delivered_count: number
    opened_count: number
    clicked_count: number
    replied_count: number
    bounced_count: number
    successful_count: number
    unsubscribed_recipients_count: number
    open_rate: number
    click_rate: number
    reply_rate: number
    bounce_rate: number
    unsubscribe_rate: number
    successful_rate: number
    tracked: boolean
    tracked_links: boolean
  }
  followUps: FollowUp[]
  unsubscriptionsPath: string
}

export function CampaignStatisticsTable({
  campaign,
  followUps,
  unsubscriptionsPath,
}: StatisticsTableProps) {
  // Generate hint HTML using JavaScript translations
  const unsubscribeHintHtml = i18n.t(
    "js.campaigns.stats.index.unsubscribed_hint_html",
    {
      path: unsubscriptionsPath,
    },
  )
  const highBounceRateHintHtml = i18n.t(
    "js.campaigns.stats.index.high_bounce_rate_hint_html",
  )

  const formatPercentage = (rate: number, count: number) => {
    return `${(rate * 100).toFixed(1)}% (${count})`
  }

  return (
    <TooltipProvider>
      <Table size="sm" className="campaign-statistics-table">
        <TableHeader>
          <TableRow>
            <TableHead></TableHead>
            <TableHead className="text-end">
              {i18n.t("js.sending_status.pending")}
            </TableHead>
            <TableHead className="text-end">
              {i18n.t("js.sending_status.sent")}
            </TableHead>
            <TableHead className="text-end">
              {i18n.t("js.sending_status.delivered")}
            </TableHead>
            {campaign.tracked && (
              <TableHead className="text-end">
                {i18n.t("js.sending_status.opened")}
              </TableHead>
            )}
            {campaign.tracked_links && (
              <TableHead className="text-end">
                {i18n.t("js.sending_status.clicked")}
              </TableHead>
            )}
            <TableHead className="text-end">
              {i18n.t("js.sending_status.replied")}
            </TableHead>
            <TableHead className="text-end">
              {i18n.t("js.sending_status.successful")}
            </TableHead>
            <TableHead className="text-end">
              {i18n.t("js.sending_status.bounced")}
            </TableHead>
            <TableHead className="text-end">
              {i18n.t("js.sending_status.unsubscribed")}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Icon
                    icon="question-circle"
                    size="sm"
                    className="question-circle ml-1"
                    aria-label="Unsubscribe information"
                  />
                </TooltipTrigger>
                <TooltipContent side="top">
                  <div
                    dangerouslySetInnerHTML={{ __html: unsubscribeHintHtml }}
                  />
                </TooltipContent>
              </Tooltip>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell>
              <strong>{i18n.t("js.campaigns.stats.index.campaign")}</strong>
            </TableCell>
            <TableCell className="text-end">
              <strong className="grey">{campaign.pending_count}</strong>
            </TableCell>
            <TableCell className="text-end">
              <strong className="blue">{campaign.sent_count}</strong>
            </TableCell>
            <TableCell className="text-end">
              <strong className="blue">{campaign.delivered_count}</strong>
            </TableCell>
            {campaign.tracked && (
              <TableCell className="text-end">
                <strong className="green">
                  {formatPercentage(campaign.open_rate, campaign.opened_count)}
                </strong>
              </TableCell>
            )}
            {campaign.tracked_links && (
              <TableCell className="text-end">
                <strong className="cyan">
                  {formatPercentage(
                    campaign.click_rate,
                    campaign.clicked_count,
                  )}
                </strong>
              </TableCell>
            )}
            <TableCell className="text-end">
              <strong className="purple">
                {formatPercentage(campaign.reply_rate, campaign.replied_count)}
              </strong>
            </TableCell>
            <TableCell className="text-end">
              <strong className="emerald">
                {formatPercentage(
                  campaign.successful_rate,
                  campaign.successful_count,
                )}
              </strong>
            </TableCell>
            <TableCell className="text-end">
              <strong className="black">
                {formatPercentage(campaign.bounce_rate, campaign.bounced_count)}
              </strong>
              {campaign.bounce_rate > 0.1 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Icon
                      icon="question-circle"
                      size="sm"
                      className="question-circle ml-1"
                      aria-label="High bounce rate information"
                    />
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: highBounceRateHintHtml,
                      }}
                    />
                  </TooltipContent>
                </Tooltip>
              )}
            </TableCell>
            <TableCell className="text-end">
              <strong className="black">
                {formatPercentage(
                  campaign.unsubscribe_rate,
                  campaign.unsubscribed_recipients_count,
                )}
              </strong>
            </TableCell>
          </TableRow>
          {followUps.map((followUp) => (
            <TableRow key={followUp.step}>
              <TableCell>{followUp.name}</TableCell>
              <TableCell className="text-end">
                <div className="grey">{followUp.pending}</div>
              </TableCell>
              <TableCell className="text-end">
                <div className="blue">{followUp.messages_sent}</div>
              </TableCell>
              <TableCell className="text-end">
                <div className="blue">{followUp.messages_delivered}</div>
              </TableCell>
              {campaign.tracked && (
                <TableCell className="text-end">
                  <div className="green">
                    {formatPercentage(followUp.open_rate, followUp.opens)}
                  </div>
                </TableCell>
              )}
              {campaign.tracked_links && (
                <TableCell className="text-end">
                  <div className="cyan">
                    {formatPercentage(followUp.click_rate, followUp.clicked)}
                  </div>
                </TableCell>
              )}
              <TableCell className="text-end">
                <div className="purple">
                  {formatPercentage(followUp.reply_rate, followUp.replied)}
                </div>
              </TableCell>
              <TableCell className="text-end">
                <div className="emerald">
                  {formatPercentage(
                    followUp.successful_rate,
                    followUp.successful,
                  )}
                </div>
              </TableCell>
              <TableCell className="text-end">
                <div className="black">
                  {formatPercentage(followUp.bounce_rate, followUp.bounced)}
                </div>
                {followUp.bounce_rate > 0.1 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Icon
                        icon="question-circle"
                        size="sm"
                        className="question-circle"
                        aria-label="High bounce rate information"
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: highBounceRateHintHtml,
                        }}
                      />
                    </TooltipContent>
                  </Tooltip>
                )}
              </TableCell>
              <TableCell className="text-end">
                <div className="black">
                  {formatPercentage(
                    followUp.unsubscribe_rate,
                    followUp.unsubscribed,
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TooltipProvider>
  )
}
